/**
 * The Fannie Mae Form 1076 / Freddie Mac Form 476 questionnaire, as data.
 *
 * This is the form nearly every lender sends an HOA when someone applies for a
 * mortgage on a unit. Banks rarely invent their own — Chase included — so
 * building this one covers them all.
 *
 * One definition, four consumers:
 *   - scripts/build-lender-questionnaire.mjs        the form (steps, sections, fields)
 *   - scripts/build-lender-questionnaire-output.mjs the lines the Process screen edits
 *   - the Project Details tab on an association      the same questions, answered once
 *   - the PDF generator                              which AcroForm field each answer lands in
 *
 * Who answers what — `by`:
 *   requestor   the person placing the order. Only the Lender table: six fields,
 *               blank. Nothing else is shown to the buyer.
 *   system      the platform, from the order itself. Resolved by name in
 *               supabase/functions/_shared/systemFieldValues.ts; a name that is
 *               not in that catalogue simply stays blank.
 *   operations  the association or its management company, on the Process
 *               screen, seeded from the association's Project Details.
 *
 * Where each answer goes in the official PDF — `pdf`:
 *   "Lender Name"                       a text field, by its AcroForm name
 *   ["Name of Preparer", "… Add"]       the same answer printed in two places
 *   { radio: "2" }                      a Yes/No pair whose states are Yes and No
 *   { radio: "6", states: { Yes: "Choice1" } }
 *                                       a radio group whose state is not named
 *                                       after the option; unlisted options map
 *                                       to a state of the same name. An empty
 *                                       answer leaves the group untouched —
 *                                       that is the "none" option, and it is
 *                                       the editors' "Not answered", never a
 *                                       literal "N/A"
 *   "8a" on a checkbox field            checked when the answer is Yes
 *
 * The names come from the AcroForm itself (acroform.json, generated from the
 * PDF), typos included: "delinquet", "bonda fide", "commerical". They are what
 * the PDF answers to, so they are not corrected here.
 *
 * Values are strings everywhere. A checkbox is "Yes" or "No"; "" means it was
 * never answered, which matters because an unanswered line takes the
 * association's default and an answered one does not.
 *
 * Kept as a spec rather than a migration on purpose: a form is content, not
 * schema. Creating a questionnaire in production is a product decision, not a
 * deployment step.
 *
 * Source: https://singlefamily.fanniemae.com/media/15656/display
 * (March 2016, addendum added December 2021)
 */

/** filled_by values the platform understands. */
export const SYSTEM = "system";
export const OPERATIONS = "operations";
export const REQUESTOR = "requestor";

/** The official form, kept under public/forms so the browser can fetch it. */
export const PDF_FILE = "fannie-mae-1076-2021-12.pdf";

/**
 * PDF fields the platform fills from the order rather than from a form field.
 * `return_by` is the order's promised delivery date — the "Complete and return
 * this form by" line the lender would otherwise write in by hand.
 */
export const PDF_FROM_CONTEXT = {
  Date: "return_by",
};

const YES_NO = ["Yes", "No"];

/** A Yes/No radio group whose two states are named Yes and No. */
const yesNo = (radio) => ({ radio });

/**
 * The paper form has two four-row grids; the form builder has no table type,
 * so each cell is a field. Row 1 of each grid carries the plain AcroForm name
 * and rows 2–4 carry a " 2", " 3", " 4" suffix — except one column of the
 * commercial grid, whose first row is named differently from the rest.
 */
function multiUnitOwnerRows() {
  const rows = [];
  for (let i = 1; i <= 4; i++) {
    const s = i === 1 ? "" : ` ${i}`;
    rows.push(
      { label: `Owner ${i} — individual or entity name`, name: `own_multi_${i}_name`, type: "text", by: OPERATIONS, pdf: `Individual / Entity Name${s}` },
      { label: `Owner ${i} — developer or sponsor?`, name: `own_multi_${i}_developer`, type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo(String(40 + i)) },
      { label: `Owner ${i} — number of units owned`, name: `own_multi_${i}_units`, type: "text", by: OPERATIONS, pdf: `Number of Units Owned${s}` },
      { label: `Owner ${i} — percentage owned of total project units`, name: `own_multi_${i}_pct`, type: "text", by: OPERATIONS, pdf: `% Owned of Total Project Units${s}` },
      { label: `Owner ${i} — number leased at market rent`, name: `own_multi_${i}_market`, type: "text", by: OPERATIONS, pdf: `Number Leased at Market Rent${s}` },
      { label: `Owner ${i} — number leased under rent control`, name: `own_multi_${i}_rent_control`, type: "text", by: OPERATIONS, pdf: `Number leased under rent control${s}` },
    );
  }
  return rows;
}

function commercialUseRows() {
  const rows = [];
  for (let i = 1; i <= 4; i++) {
    const s = i === 1 ? "" : ` ${i}`;
    rows.push(
      { label: `Use ${i} — type of commercial or non-residential use`, name: `own_commercial_${i}_type`, type: "text", by: OPERATIONS, pdf: `Type of Use${s}` },
      { label: `Use ${i} — name of owner or tenant`, name: `own_commercial_${i}_owner`, type: "text", by: OPERATIONS, pdf: i === 1 ? "Name of Owner or Tenant" : `Name of Owner/Tenant${s}` },
      { label: `Use ${i} — number of units`, name: `own_commercial_${i}_units`, type: "text", by: OPERATIONS, pdf: `Number of Units${s}` },
      { label: `Use ${i} — square footage`, name: `own_commercial_${i}_sqft`, type: "text", by: OPERATIONS, pdf: `Square Footage${s}` },
      { label: `Use ${i} — percentage of total project square footage`, name: `own_commercial_${i}_pct`, type: "text", by: OPERATIONS, pdf: `% Sq. Footage${s}` },
    );
  }
  return rows;
}

/**
 * Steps → sections → fields.
 *
 * `name` is the code-facing identifier: the key in Project Details, the handle
 * the output format points at, and for system fields the resolver to call.
 * `perOrder` marks an operations field that is never a default of the
 * association — it is answered fresh on every order.
 */
export const QUESTIONNAIRE = {
  documentTypeName: "Questionnaire – Chase",
  formName: "Lender Questionnaire (Fannie Mae 1076 / Freddie Mac 476)",
  steps: [
    // -----------------------------------------------------------------------
    {
      title: "Lender",
      sections: [
        {
          title: "Lender",
          subtitle: "The bank asking for this questionnaire. Filled in by whoever places the order.",
          fields: [
            { label: "Lender name", name: "lender_name", type: "text", by: REQUESTOR, pdf: "Lender Name" },
            { label: "Contact name", name: "lender_contact_name", type: "text", by: REQUESTOR, pdf: "Contact Name" },
            { label: "Lender address", name: "lender_address", type: "text", by: REQUESTOR, pdf: "Lender Address" },
            { label: "Lender phone number", name: "lender_phone", type: "text", by: REQUESTOR, pdf: "Lender Phone Number" },
            { label: "Lender fax number", name: "lender_fax", type: "text", by: REQUESTOR, pdf: "Lender Fax Number" },
            { label: "Lender email address", name: "lender_email", type: "text", by: REQUESTOR, pdf: "Lender Email Address" },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Project",
      sections: [
        {
          title: "Basic Project Information",
          subtitle: "Who the project is and who manages it. Taken from the association and its management company.",
          fields: [
            { label: "Project legal name", name: "association_name", type: "text", by: SYSTEM, pdf: ["Project Legal Name", "Project Name"] },
            { label: "Project physical address", name: "association_physical_address", type: "text", by: SYSTEM, pdf: ["Project Physical Address", "Project Address"] },
            { label: "HOA management address", name: "management_company_address", type: "text", by: SYSTEM, pdf: "HOA Management Address" },
            { label: "HOA name, if different from the project legal name", name: "hoa_name_if_different", type: "text", by: OPERATIONS, pdf: "HOA Name (if different than Project Legal Name)" },
            { label: "HOA tax ID", name: "association_ein", type: "text", by: SYSTEM, pdf: "HOA Tax ID" },
            { label: "HOA management company", name: "management_company_name", type: "text", by: SYSTEM },
            { label: "HOA management company tax ID", name: "management_company_ein", type: "text", by: OPERATIONS, pdf: "HOA Management Tax ID #",
              // The one identity line the platform cannot answer on its own:
              // management_companies has no tax-id column yet. The help is
              // written for the MC admin who fills it, never for us.
              help: "Your management company's federal tax ID (EIN). It prints on the questionnaire." },
            { label: "Name of master or umbrella association, if applicable", name: "master_association_name", type: "text", by: SYSTEM, pdf: "Name of Master or Umbrella Association" },
          ],
        },
        {
          title: "Does the project contain any of the following?",
          subtitle: "Check all that apply.",
          fields: [
            { label: "Hotel, motel or resort activities, mandatory or voluntary rental-pooling arrangements, or other restrictions on the unit owner's ability to occupy the unit", name: "q8_hotel_activity", type: "checkbox", by: OPERATIONS, pdf: "8a" },
            { label: "Deed or resale restrictions", name: "q8_deed_restrictions", type: "checkbox", by: OPERATIONS, pdf: "8b" },
            { label: "Manufactured homes", name: "q8_manufactured_homes", type: "checkbox", by: OPERATIONS, pdf: "8c" },
            { label: "Mandatory fee-based memberships for use of project amenities or services", name: "q8_mandatory_memberships", type: "checkbox", by: OPERATIONS, pdf: "8d" },
            { label: "Non-incidental income from business operations", name: "q8_business_income", type: "checkbox", by: OPERATIONS, pdf: "8e" },
            { label: "Supportive or continuing care for seniors or for residents with disabilities", name: "q8_supportive_care", type: "checkbox", by: OPERATIONS, pdf: "8f" },
            { label: "Additional detail, if applicable", name: "q8_detail", type: "text", by: OPERATIONS, pdf: "8 Provide additional detail" },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Completion",
      sections: [
        {
          title: "Project Completion Information",
          fields: [
            { label: "Is the project 100% complete, including all construction or renovation of units, common elements, and shared amenities for all project phases?", name: "completion_100_percent", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("Group1") },
            { label: "If not complete: is the project subject to additional phasing or annexation?", name: "completion_phasing", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("2") },
            { label: "Is the project legally phased?", name: "completion_legally_phased", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("3") },
            { label: "How many phases have been completed?", name: "completion_phases_done", type: "text", by: OPERATIONS, pdf: "How many phases have been completed?" },
            { label: "How many total phases are legally planned for the project?", name: "completion_phases_planned", type: "text", by: OPERATIONS, pdf: "How many total phases are legally planned for the project?" },
            { label: "How many total units are planned for the project?", name: "completion_units_planned", type: "text", by: OPERATIONS, pdf: "How many total units are planned for the project?" },
            { label: "Are all planned amenities and common facilities fully complete?", name: "completion_amenities_done", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("4") },
            { label: "Has the developer transferred control of the HOA to the unit owners?", name: "completion_control_transferred", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("5") },
            { label: "If yes: date control was transferred", name: "completion_control_date", type: "date", by: OPERATIONS, pdf: "Date transferred" },
            { label: "If no: estimated date the transfer will occur", name: "completion_control_expected_date", type: "date", by: OPERATIONS, pdf: "Estimated date transfer" },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Conversion",
      sections: [
        {
          title: "Newly Converted or Rehabilitated Project",
          subtitle: "Only if the project was converted in the past three years.",
          fields: [
            { label: "Is the project a conversion within the past 3 years of an existing structure that was used as an apartment, hotel/resort, retail or professional business, industrial or for other non-residential use?", name: "conversion_recent", type: "select", by: OPERATIONS, options: YES_NO, pdf: { radio: "6", states: { Yes: "Choice1" } } },
            { label: "In what year was the property built?", name: "conversion_year_built", type: "text", by: OPERATIONS, pdf: "In what year was the property built?" },
            { label: "In what year was the property converted?", name: "conversion_year_converted", type: "text", by: OPERATIONS, pdf: "In what year was the property converted?" },
            { label: "Was the conversion a full gut rehabilitation of the existing structure(s), including replacement of all major mechanical components?", name: "conversion_gut_rehab", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("7") },
            { label: "Does the report from the licensed engineer indicate that the project is structurally sound, and that the condition and remaining useful life of the project's major components are sufficient?", name: "conversion_engineer_report", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("8") },
            { label: "Are all repairs affecting safety, soundness, and structural integrity complete?", name: "conversion_repairs_done", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("10") },
            { label: "Are replacement reserves allocated for all capital improvements?", name: "conversion_reserves_allocated", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("11") },
            { label: "Are the project's reserves sufficient to fund the improvements?", name: "conversion_reserves_sufficient", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("12") },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Financial",
      sections: [
        {
          title: "Financial Information",
          fields: [
            { label: "How many unit owners are 60 or more days delinquent on common expense assessments?", name: "fin_delinquent_60_days", type: "text", by: OPERATIONS, pdf: "How many unit owners are 60 or more days delinquet?" },
            { label: "In the event a lender acquires a unit due to foreclosure or a deed-in-lieu of foreclosure, is the mortgagee responsible for paying delinquent common expense assessments?", name: "fin_lender_liable", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("21") },
            { label: "If yes, for how long is the mortgagee responsible for paying common expense assessments?", name: "fin_lender_liable_months", type: "select", by: OPERATIONS,
              options: ["1 to 6 months", "7 to 12 months", "More than 12 months"], pdf: { radio: "22" } },
            { label: "Is the HOA involved in any active or pending litigation?", name: "fin_litigation", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("23") },
            { label: "If yes: attorney's name", name: "fin_attorney_name", type: "text", by: OPERATIONS, pdf: "Attorney Name",
              help: "Attach documentation regarding the litigation from the attorney or the HOA." },
            { label: "Attorney's phone number", name: "fin_attorney_phone", type: "text", by: OPERATIONS, pdf: "Attorney Phone Number" },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Ownership",
      sections: [
        {
          title: "Ownership of units — entire project",
          subtitle: "The left column of the ownership grid.",
          fields: [
            { label: "Total number of units", name: "own_total_units", type: "text", by: OPERATIONS, pdf: "Total no. of units" },
            { label: "Total number of units sold and closed", name: "own_sold_closed", type: "text", by: OPERATIONS, pdf: "Total no. of units sold and closed" },
            { label: "Total number of units under bona-fide sales contracts", name: "own_under_contract", type: "text", by: OPERATIONS, pdf: "Total no. of units under bonda fide" },
            { label: "Total number of units sold and closed or under contract to owner-occupants", name: "own_owner_occupants", type: "text", by: OPERATIONS, pdf: "Total no. of units sold and closed under contract to owner-occupants" },
            { label: "Total number of units sold and closed or under contract to second home owners", name: "own_second_homes", type: "text", by: OPERATIONS, pdf: "Total no. of units to second home owners" },
            { label: "Total number of units sold and closed or under contract to investor owners", name: "own_investors", type: "text", by: OPERATIONS, pdf: "Total no. of units sold and closed or under contract to investor owners" },
            { label: "Total number of units being rented by developer, sponsor, or converter", name: "own_developer_rented", type: "text", by: OPERATIONS, pdf: "Total no. of units rented" },
            { label: "Total number of units owned by the HOA", name: "own_hoa_owned", type: "text", by: OPERATIONS, pdf: "Total no. of units owned by HOA" },
          ],
        },
        {
          title: "Ownership of units — subject legal phase",
          subtitle: "The right column: the phase the unit is in, if applicable. Leave blank if the project is not phased.",
          fields: [
            { label: "Total number of units in the phase", name: "phase_total_units", type: "text", by: OPERATIONS, pdf: "Total no. of units Subj. Legal Phase" },
            { label: "Units sold and closed in the phase", name: "phase_sold_closed", type: "text", by: OPERATIONS, pdf: "Total no. of units sold and closed Subj. Legal Phase" },
            { label: "Units under bona-fide sales contracts in the phase", name: "phase_under_contract", type: "text", by: OPERATIONS, pdf: "Total no. of units under bonda fide Subj. Legal Phase" },
            { label: "Units sold and closed or under contract to owner-occupants in the phase", name: "phase_owner_occupants", type: "text", by: OPERATIONS, pdf: "Total no. of units sold and closed under contract to owner-occupants Subj. Legal Phase" },
            { label: "Units sold and closed or under contract to second home owners in the phase", name: "phase_second_homes", type: "text", by: OPERATIONS, pdf: "Total no. of units to second home owners Subj. Legal Phase" },
            { label: "Units sold and closed or under contract to investor owners in the phase", name: "phase_investors", type: "text", by: OPERATIONS, pdf: "Total no. of units sold and closed or under contract to investor owners Subj. Legal Phase" },
            { label: "Units being rented by developer, sponsor, or converter in the phase", name: "phase_developer_rented", type: "text", by: OPERATIONS, pdf: "Total no. of units rented Subj. Legal Phase" },
            { label: "Units owned by the HOA in the phase", name: "phase_hoa_owned", type: "text", by: OPERATIONS, pdf: "Total no. of units owned by HOA Subj. Legal Phase" },
          ],
        },
        {
          title: "Owners of more than one unit",
          subtitle: "Complete if more than one unit is owned by the same individual or entity. Up to four owners.",
          fields: [
            { label: "Does any individual or entity own more than one unit?", name: "own_multi_unit_owner", type: "select", by: OPERATIONS, options: YES_NO },
            ...multiUnitOwnerRows(),
          ],
        },
        {
          title: "Amenities and common areas",
          fields: [
            { label: "Do the unit owners have sole ownership interest in and the right to use the project amenities and common areas?", name: "own_sole_interest", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("45") },
            { label: "If no, explain who has ownership interest in and rights to use the project amenities and common areas", name: "own_sole_interest_explanation", type: "text", by: OPERATIONS, pdf: "3a no explanation" },
          ],
        },
        {
          title: "Commercial or non-residential space",
          subtitle: "Complete the table if any units or any part of the building are used for non-residential or commercial space. Up to four uses.",
          fields: [
            { label: "Are any units or any part of the building used for non-residential or commercial space?", name: "own_commercial_use", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("46") },
            ...commercialUseRows(),
          ],
        },
        {
          title: "Total commercial space",
          fields: [
            { label: "Total square footage of commercial space in the building that is separate from the residential HOA", name: "own_commercial_sqft", type: "text", by: OPERATIONS, pdf: "Total sq. footage of commerical space",
              help: "Include above and below grade space used for commercial purposes: public parking, retail, apartments, commercial offices." },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Insurance & controls",
      sections: [
        {
          title: "Flood",
          fields: [
            { label: "Are units or common elements located in a flood zone?", name: "ins_flood_zone", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("51") },
            { label: "If yes, flood coverage is in force equaling", name: "ins_flood_coverage", type: "select", by: OPERATIONS,
              options: ["100% replacement cost", "Maximum coverage per condominium available under the NFIP", "Some other amount"],
              pdf: { radio: "52", states: { "Maximum coverage per condominium available under the NFIP": "Maximum coverage per condominium available under the National Flood Insurance Program" } } },
            { label: "If some other amount, enter the amount ($)", name: "ins_flood_amount", type: "text", by: OPERATIONS, pdf: "Some other amount Enter amount here" },
          ],
        },
        {
          title: "HOA financial controls",
          subtitle: "Check all that apply. Lenders read these individually.",
          fields: [
            { label: "HOA maintains separate accounts for operating and reserve funds", name: "ctl_separate_accounts", type: "checkbox", by: OPERATIONS, pdf: "121" },
            { label: "Appropriate access controls are in place for each account", name: "ctl_access_controls", type: "checkbox", by: OPERATIONS, pdf: "45465" },
            { label: "The bank sends copies of monthly bank statements directly to the HOA", name: "ctl_statements_to_hoa", type: "checkbox", by: OPERATIONS, pdf: "5545645" },
            { label: "Two members of the HOA Board of Directors are required to sign any check written on the reserve account", name: "ctl_two_signatures", type: "checkbox", by: OPERATIONS, pdf: "554545" },
            { label: "The Management Company maintains separate records and bank accounts for each HOA that uses its services", name: "ctl_separate_records", type: "checkbox", by: OPERATIONS, pdf: "55151" },
            { label: "The Management Company does not have the authority to draw checks on, or transfer funds from, the reserve account of the HOA", name: "ctl_no_mgmt_withdrawals", type: "checkbox", by: OPERATIONS, pdf: "5522312" },
          ],
        },
        {
          title: "Policies in force",
          subtitle: 'The form is explicit: do not answer "contact agent".',
          fields: [
            { label: "Hazard — carrier or agent name", name: "ins_hazard_carrier", type: "text", by: OPERATIONS, pdf: "Hazard - Carrier/Agent Name" },
            { label: "Hazard — carrier or agent phone number", name: "ins_hazard_phone", type: "text", by: OPERATIONS, pdf: "Hazard - Carrier/Agent Phone" },
            { label: "Hazard — policy number", name: "ins_hazard_policy", type: "text", by: OPERATIONS, pdf: "Hazard - Carrier/Agent Policy Number" },
            { label: "Liability — carrier or agent name", name: "ins_liability_carrier", type: "text", by: OPERATIONS, pdf: "Liability - Carrier/Agent Name" },
            { label: "Liability — carrier or agent phone number", name: "ins_liability_phone", type: "text", by: OPERATIONS, pdf: "Liability - Carrier/Agent Phone" },
            { label: "Liability — policy number", name: "ins_liability_policy", type: "text", by: OPERATIONS, pdf: "Liability- Carrier/Agent Policy Number" },
            { label: "Fidelity — carrier or agent name", name: "ins_fidelity_carrier", type: "text", by: OPERATIONS, pdf: "Fidelity - Carrier/Agent Name" },
            { label: "Fidelity — carrier or agent phone number", name: "ins_fidelity_phone", type: "text", by: OPERATIONS, pdf: "Fidelity - Carrier/Agent Phone" },
            { label: "Fidelity — policy number", name: "ins_fidelity_policy", type: "text", by: OPERATIONS, pdf: "Fidelity - Carrier/Agent Policy Number" },
            { label: "Flood — carrier or agent name", name: "ins_flood_carrier", type: "text", by: OPERATIONS, pdf: "Flood - Carrier/Agent Name" },
            { label: "Flood — carrier or agent phone number", name: "ins_flood_phone", type: "text", by: OPERATIONS, pdf: "Flood - Carrier/Agent Phone" },
            { label: "Flood — policy number", name: "ins_flood_policy", type: "text", by: OPERATIONS, pdf: "Flood - Carrier/Agent Policy Number" },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Building safety",
      sections: [
        {
          title: "Inspections and deficiencies",
          subtitle: "Addendum added December 2021: Building Safety, Soundness, Structural Integrity, and Habitability.",
          fields: [
            { label: "When was the last building inspection by a licensed architect, licensed engineer, or any other building inspector?", name: "bs_last_inspection", type: "text", by: OPERATIONS, pdf: "When was the last building inspection by a licensed architect, licensed engineer, or any other building inspector?" },
            { label: "Did the last inspection have any findings related to the safety, soundness, structural integrity, or habitability of the project's building(s)?", name: "bs_inspection_findings", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("61") },
            { label: "If yes, have recommended repairs/replacements been completed?", name: "bs_repairs_completed", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("62") },
            { label: "If not completed: what repairs/replacements remain to be completed?", name: "bs_repairs_remaining", type: "text", by: OPERATIONS, pdf: "2b" },
            { label: "When will the repairs/replacements be completed?", name: "bs_repairs_when", type: "text", by: OPERATIONS, pdf: "2c",
              help: "Provide a copy of the inspection and HOA or cooperative board meeting minutes to document findings and action plan." },
            { label: "Is the HOA aware of any deficiencies related to the safety, soundness, structural integrity, or habitability of the project's building(s)?", name: "bs_known_deficiencies", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("63") },
            { label: "If yes, what are the deficiencies?", name: "bs_deficiencies_detail", type: "text", by: OPERATIONS, pdf: "3a" },
            { label: "Of these deficiencies, what repairs/replacements remain to be completed?", name: "bs_deficiencies_repairs_remaining", type: "text", by: OPERATIONS, pdf: "3b" },
            { label: "Of these deficiencies, when will the repairs/replacements be completed?", name: "bs_deficiencies_repairs_when", type: "text", by: OPERATIONS, pdf: "3c" },
          ],
        },
        {
          title: "Violations, maintenance and reserves",
          fields: [
            { label: "Are there any outstanding violations of jurisdictional requirements (zoning ordinances, codes, etc.) related to the safety, soundness, structural integrity, or habitability of the project's building(s)?", name: "bs_violations", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("71"),
              help: "If yes, provide notice from the applicable jurisdictional entity." },
            { label: "Is it anticipated the project will, in the future, have such violation(s)?", name: "bs_future_violations", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("72") },
            { label: "If yes: the applicable jurisdiction's requirement and the project's plan to remediate the violation", name: "bs_future_violations_detail", type: "text", by: OPERATIONS, pdf: "5y_explanation" },
            { label: "Does the project have a funding plan for its deferred maintenance components/items to be repaired or replaced?", name: "bs_deferred_maintenance_funding", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("73") },
            { label: "Does the project have a schedule for the deferred maintenance components/items to be repaired or replaced?", name: "bs_deferred_maintenance_schedule", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("74"),
              help: "If yes, provide the schedule." },
            { label: "Has the HOA had a reserve study completed on the project within the past 3 years?", name: "bs_reserve_study", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("75") },
            { label: "What is the total of the current reserve account balance(s)? ($)", name: "bs_reserve_balance", type: "text", by: OPERATIONS, pdf: "9" },
          ],
        },
        {
          title: "Special assessments and loans",
          fields: [
            { label: "Are there any current special assessments unit owners are obligated to pay?", name: "bs_current_special_assessments", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("76") },
            { label: "If yes: total amount of the current special assessment(s) ($)", name: "bs_current_sa_amount", type: "text", by: OPERATIONS, pdf: "10a" },
            { label: "Terms of the current special assessment(s)", name: "bs_current_sa_terms", type: "text", by: OPERATIONS, pdf: "10b" },
            { label: "Purpose of the current special assessment(s)", name: "bs_current_sa_purpose", type: "text", by: OPERATIONS, pdf: "10c" },
            { label: "Are there any planned special assessments that unit owners will be obligated to pay?", name: "bs_planned_special_assessments", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("81") },
            { label: "If yes: total amount of the planned special assessments ($)", name: "bs_planned_sa_amount", type: "text", by: OPERATIONS, pdf: "11a" },
            { label: "Terms of the planned special assessments", name: "bs_planned_sa_terms", type: "text", by: OPERATIONS, pdf: "11b" },
            { label: "Purpose of the planned special assessments", name: "bs_planned_sa_purpose", type: "text", by: OPERATIONS, pdf: "11c" },
            { label: "Has the HOA obtained any loans to finance improvements or deferred maintenance?", name: "bs_hoa_loans", type: "select", by: OPERATIONS, options: YES_NO, pdf: yesNo("82") },
            { label: "If yes: amount borrowed ($)", name: "bs_loan_amount", type: "text", by: OPERATIONS, pdf: "12a" },
            { label: "Terms of repayment", name: "bs_loan_terms", type: "text", by: OPERATIONS, pdf: "12b" },
          ],
        },
        {
          title: "Additional comments",
          fields: [
            { label: "Additional comments", name: "bs_additional_comments", type: "text", by: OPERATIONS, pdf: "Additional Comments" },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Preparer",
      sections: [
        {
          title: "Contact Information",
          subtitle: "Who at the management company answered this. Printed on page 5 and again on the addendum.",
          fields: [
            { label: "Name of preparer", name: "prep_name", type: "text", by: OPERATIONS, pdf: ["Name of Preparer", "Name of Preparer Add"] },
            { label: "Title of preparer", name: "prep_title", type: "text", by: OPERATIONS, pdf: ["Title of Preparer", "Title of Preparer Add"] },
            { label: "Preparer's phone", name: "prep_phone", type: "text", by: OPERATIONS, pdf: ["Preparer’s Phone", "Preparers Phone Add"] },
            { label: "Preparer's email", name: "prep_email", type: "text", by: OPERATIONS, pdf: ["Preparer’s Email", "Preparers Email Add"] },
            { label: "Preparer's company name", name: "prep_company", type: "text", by: OPERATIONS, pdf: ["Preparer’s Company Name", "Preparers Company Name Add"],
              defaultFrom: "management_company_name" },
            { label: "Preparer's company address", name: "prep_company_address", type: "text", by: OPERATIONS, pdf: ["Preparer’s Company Address", "Preparers Company Address Add"],
              defaultFrom: "management_company_address" },
            { label: "Date completed", name: "prep_date", type: "date", by: OPERATIONS, pdf: ["Date Completed", "Date Completed Add"], perOrder: true },
          ],
        },
      ],
    },
  ],
};

/** Every field, flattened, in the order they appear. */
export function allFields() {
  const out = [];
  QUESTIONNAIRE.steps.forEach((step, si) =>
    step.sections.forEach((section, ci) =>
      section.fields.forEach((field, fi) =>
        out.push({ ...field, stepIndex: si, sectionIndex: ci, fieldIndex: fi,
                   stepTitle: step.title, sectionTitle: section.title })
      )
    )
  );
  return out;
}

/**
 * The questions an association answers once, in Project Details: everything
 * the operations side fills in, minus what is decided per order.
 */
export function projectDetailFields() {
  return allFields().filter((f) => f.by === OPERATIONS && !f.perOrder);
}

/** The PDF fields a form field writes to, always as a list. */
export function pdfTargets(field) {
  if (!field.pdf) return [];
  if (typeof field.pdf === "string") return [field.pdf];
  if (Array.isArray(field.pdf)) return field.pdf;
  return [field.pdf.radio];
}

/**
 * For a select mapped to a radio group: the AcroForm state an option selects,
 * or null when that option leaves the group untouched. "N/A" is kept only for
 * answers stored before the option was removed.
 */
export function radioState(field, option) {
  if (!field.pdf || typeof field.pdf !== "object" || Array.isArray(field.pdf)) return null;
  if (option === "N/A" || option === "") return null;
  const states = field.pdf.states ?? {};
  return states[option] ?? option;
}
